import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from "./Dialog";
import { Button } from "../atoms/Button";

/**
 * ConfirmDialog — manifest §ConfirmDialog.
 *
 *   <ConfirmDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     title="Delete plot?"
 *     body="This cannot be undone."
 *     destructive
 *     confirmLabel="Delete"
 *     loading={isDeleting}
 *     onConfirm={handleConfirm}
 *   />
 *
 * Confirm runs amber `primary` (or red `danger` when destructive) with an
 * optional loading state that also disables the ghost cancel button. The
 * caller owns `open` — close via `onOpenChange` after `onConfirm` settles.
 */
export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  body: React.ReactNode;
  /** Confirm button label. */
  confirmLabel?: string;
  /** Cancel button label. */
  cancelLabel?: string;
  /** Red confirm button for irreversible actions. */
  destructive?: boolean;
  onConfirm: () => void;
  /** Confirm button loading state; cancel is disabled while loading. */
  loading?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  loading = false,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>{title}</DialogHeader>
        <DialogBody>{body}</DialogBody>
        <DialogFooter>
          <Button
            variant="ghost"
            disabled={loading}
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
ConfirmDialog.displayName = "ConfirmDialog";
